import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { fetchNoBillState, saveNoBillMapping, type NoBillStateDTO } from "../../data/noBillApi";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { CheckIcon } from "../../components/icons";
import { cn } from "../../components/cn";

/**
 * The client already does this, under their own names.
 *
 * Plenty of books have carried undocumented spend for years in something called
 * `Petty Cash Expenses` or `Sundry Expenses`. Those accounts are not wrong and
 * there is nothing to create — the only thing missing is that nobody has written
 * down which ones they are.
 *
 * NOTHING HERE TOUCHES ZOHO. No rename, no re-parent, no new account, not on save
 * and not on any later push. Renaming them into our naming was the alternative
 * and it does not work: a rename does not move the transactions already posted,
 * so it leaves one ledger holding vouched and unvouched history under a name
 * claiming otherwise, and rewrites how every past report reads.
 *
 * Which is why this panel has no confirm and no "whose books" warning. There is
 * nothing to be careful about — unticking is as free as ticking.
 */
export function NoBillMappingPanel({
  clientId,
  clientName,
}: {
  clientId: ClientId;
  clientName: string;
}) {
  const [state, setState] = useState<NoBillStateDTO | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;

    fetchNoBillState(clientId)
      .then((result) => {
        if (!liveRef.current) return;
        setState(result);
        setPicked(new Set(result.mapped.map((row) => row.account_id)));
      })
      .catch((cause: unknown) => {
        if (liveRef.current) {
          setError(cause instanceof Error ? cause.message : "Could not read the ledgers.");
        }
      });

    return () => {
      liveRef.current = false;
    };
  }, [clientId]);

  function toggle(accountId: string) {
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);

      return next;
    });
  }

  function save() {
    setBusy(true);
    setError(null);

    saveNoBillMapping(clientId, Array.from(picked))
      .then((result) => {
        setState(result);
        setPicked(new Set(result.mapped.map((row) => row.account_id)));
        setSavedAt(new Date().toLocaleTimeString());
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not save the mapping.");
      })
      .finally(() => setBusy(false));
  }

  if (state === null) {
    return (
      <div className="flex flex-col gap-3 rounded-md border p-4">
        <span className="text-xs font-medium text-muted-foreground">Their No Bill ledgers</span>
        {error !== null ? (
          <Callout variant="warning">{error}</Callout>
        ) : (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
      </div>
    );
  }

  if (!state.chart_read) {
    return (
      <div className="flex flex-col gap-3 rounded-md border p-4">
        <span className="text-xs font-medium text-muted-foreground">Their No Bill ledgers</span>
        <Callout variant="warning">
          We have not read {clientName}'s chart of accounts yet, so there is nothing to point at.
        </Callout>
      </div>
    );
  }

  const query = filter.trim().toLowerCase();
  // A ticked ledger is never filtered out of reach: one hidden by a filter is one
  // the CA cannot untick, and saving would keep it without their meaning to.
  const shown = state.candidates.filter(
    (candidate) => query === "" || candidate.label.toLowerCase().includes(query) || picked.has(candidate.account_id)
  );
  const stored = new Set(state.mapped.map((row) => row.account_id));
  const unsaved =
    picked.size !== stored.size || Array.from(picked).some((id) => !stored.has(id));
  /**
   * Mapped ledgers that no longer match what Zoho holds.
   *
   * Inactive belongs here and not only for tidiness: the picker below is built
   * from active ledgers, so an inactive one has no checkbox at all — it would sit
   * in the stored set, invisible, with no way to take it off. This callout is the
   * only place it can be seen.
   */
  const drifted = state.mapped.filter((row) => row.renamed || row.missing || !row.active);

  return (
    <div className="flex flex-col gap-4 rounded-md border p-4">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Their No Bill ledgers</span>
        {/* One line, for the same reason as the create panel: the decision card
            above has already said what this does. */}
        <p className="text-sm text-muted-foreground">
          Tick the ledgers they already use when there is no bill. Nothing in their books changes.
        </p>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {/* Something has changed in Zoho since the decision was made. Said out
          loud, because a mapping that quietly follows a rename is a mapping
          nobody can audit. */}
      {drifted.length > 0 && (
        <Callout variant="warning">
          {drifted.map((row) => (
            <span key={row.account_id} className="block">
              {row.missing
                ? `"${row.account_name}" is no longer in the chart of accounts. Untick it, or refresh the chart if it was only just added.`
                : row.renamed
                  ? `"${row.account_name}" is now called "${row.current_name}" in Zoho. Tick it again to record the new name.`
                  : `"${row.account_name}" is marked inactive in Zoho, so it is not in the list below. Make it active there, or clear it with Save.`}
            </span>
          ))}
        </Callout>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">{clientName}'s expense ledgers</span>

        {state.candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No expense ledgers were read from their books, so there is nothing to point at yet.
          </p>
        ) : (
          <>
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={`Filter ${state.candidates.length} ledgers`}
              disabled={busy}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            />
            {shown.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing matches "{filter.trim()}".</p>
            ) : (
              <ul className="grid max-h-64 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2">
                {shown.map((candidate) => {
                  const checked = picked.has(candidate.account_id);

                  return (
                    <li key={candidate.account_id}>
                      <label
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors",
                          checked ? "border-primary/50 bg-primary/5" : "hover:bg-muted/40"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => toggle(candidate.account_id)}
                          className="size-3.5 shrink-0 accent-primary"
                        />
                        <span className="truncate">{candidate.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        <CoButton onClick={save} disabled={busy || !unsaved}>
          {busy
            ? "Saving…"
            : picked.size === 0
              ? "Save - none of them"
              : `Save ${picked.size} ${picked.size === 1 ? "ledger" : "ledgers"}`}
        </CoButton>
        <span className="text-xs text-muted-foreground">
          {unsaved
            ? "Not saved yet."
            : savedAt !== null
              ? `Saved at ${savedAt}.`
              : state.mapped.length === 0
                ? "Nothing recorded yet."
                : `${state.mapped.length} ${state.mapped.length === 1 ? "ledger" : "ledgers"} recorded.`}
        </span>
      </div>

      {/* What is stored, under its stored name, so the record is visible rather
          than only inferrable from which boxes are ticked. */}
      {state.mapped.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 border-t pt-3">
          {state.mapped.map((row) => (
            <li
              key={row.account_id}
              className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 text-sm"
            >
              <CheckIcon className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              {row.account_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
